import { useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormHelperText,
  FormLabel,
  Group,
  IconCrossCircle,
  Input,
  Select,
  Textarea,
  Typography,
  Wrapper
} from '@screentone/core';

import type { SectionParameterSchemaEntry } from './SectionParameterSettings';

const PARAMETER_TYPES = ['string', 'boolean', 'enum'] as const;

interface Props {
  initial?: SectionParameterSchemaEntry;
  onSave: (entry: SectionParameterSchemaEntry) => void;
  onCancel: () => void;
}

export const SectionParameterSchemaForm = ({ initial, onSave, onCancel }: Props) => {
  const [key, setKey] = useState(initial?.key ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [type, setType] = useState<string>(initial?.type ?? 'string');
  const [enumValuesRaw, setEnumValuesRaw] = useState((initial?.enumValues ?? []).join(', '));
  const [deprecated, setDeprecated] = useState(initial?.deprecated ?? false);
  const [placeholder, setPlaceholder] = useState(initial?.placeholder ?? '');
  const [userExposed, setUserExposed] = useState(initial?.userExposed ?? false);

  const isEditing = !!initial;

  const KEY_PATTERN = /^[a-z0-9_]+$/;
  const keyError = key && !KEY_PATTERN.test(key) ? 'Key must be lowercase letters, numbers, and underscores only' : '';

  const handleSave = () => {
    const trimmedKey = key.trim();
    const trimmedLabel = label.trim();
    if (!trimmedKey || !trimmedLabel || keyError) return;

    const enumValues =
      type === 'enum'
        ? enumValuesRaw
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : undefined;

    onSave({
      key: trimmedKey,
      label: trimmedLabel,
      type,
      enumValues,
      deprecated: deprecated || undefined,
      placeholder: placeholder.trim() || undefined,
      userExposed: userExposed || undefined
    });
  };

  const isSaveDisabled = !key.trim() || !!keyError || !label.trim() || (type === 'enum' && !enumValuesRaw.trim());

  return (
    <Wrapper>
      <Typography variant="header4" margin={{ bottom: 'sm' }}>
        {isEditing ? 'Edit Parameter' : 'New Parameter'}
      </Typography>
      <Group direction="column" gap="sm">
        <FormLabel label="Key" fullWidth>
          <Group gap="xs" wrap={false} valign="center">
            <Input
              value={key}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKey(e.target.value)}
              placeholder="e.g. summary_priority"
              disabled={isEditing}
              error={!!keyError}
            />
            {!isEditing && key && <Button tertiary icon={IconCrossCircle} onClick={() => setKey('')} />}
          </Group>
          {keyError ? (
            <FormHelperText error>{keyError}</FormHelperText>
          ) : (
            <FormHelperText>Use underscores to separate words, e.g. summary_priority</FormHelperText>
          )}
        </FormLabel>
        <FormLabel label="Label" fullWidth>
          <Group gap="xs" wrap={false} valign="center">
            <Input
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
              placeholder="e.g. Summary Priority"
            />
            {label && <Button tertiary icon={IconCrossCircle} onClick={() => setLabel('')} />}
          </Group>
        </FormLabel>
        <FormLabel label="Placeholder" fullWidth>
          <Group gap="xs" wrap={false} valign="center">
            <Input
              value={placeholder}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlaceholder(e.target.value)}
              placeholder="e.g. Enter a value"
            />
            {placeholder && <Button tertiary icon={IconCrossCircle} onClick={() => setPlaceholder('')} />}
          </Group>
        </FormLabel>
        <FormLabel label="Type" fullWidth>
          <Select value={type} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setType(e.target.value)}>
            {PARAMETER_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </FormLabel>
        {type === 'enum' && (
          <FormLabel label="Enum Values (comma-separated)" fullWidth>
            <Group gap="xs" wrap={false} valign="center">
              <Textarea
                value={enumValuesRaw}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEnumValuesRaw(e.target.value)}
                placeholder="e.g. HIGH, MEDIUM, LOW"
                rows={2}
              />
              {enumValuesRaw && <Button tertiary icon={IconCrossCircle} onClick={() => setEnumValuesRaw('')} />}
            </Group>
          </FormLabel>
        )}
        <FormLabel label="User Exposed" labelPosition="right" fullWidth>
          <Checkbox
            checked={userExposed}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUserExposed(e.target.checked)}
          />
        </FormLabel>
        {isEditing && (
          <FormLabel label="Deprecated" labelPosition="right" fullWidth>
            <Checkbox
              checked={deprecated}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeprecated(e.target.checked)}
            />
          </FormLabel>
        )}
      </Group>
      <Divider margin={{ top: 'md', bottom: 'md' }} />
      <Group gap="sm">
        <Button secondary onClick={onCancel} margin={{ right: 'sm' }}>
          Cancel
        </Button>
        <Button primary onClick={handleSave} disabled={isSaveDisabled}>
          {isEditing ? 'Update' : 'Create'}
        </Button>
      </Group>
    </Wrapper>
  );
};
